import React, { useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Android,
  Memory,
  Schedule,
} from '@mui/icons-material';
import StatusBadge from './shared/StatusBadge';
import { tokens } from '../theme';

// ─── Barra de distribución reutilizable ────────────────────────────────────────
const DistBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: tokens.color.ink.primary }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>
          {count} <Typography component="span" variant="caption" sx={{ color: tokens.color.ink.muted }}>({pct}%)</Typography>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          '& .MuiLinearProgress-bar': { backgroundColor: color },
        }}
      />
    </Box>
  );
};

// ─── Tiempo relativo ───────────────────────────────────────────────────────────
const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60)     return 'Hace un momento';
  if (s < 3600)   return `Hace ${Math.floor(s / 60)} min`;
  if (s < 86400)  return `Hace ${Math.floor(s / 3600)} h`;
  return `Hace ${Math.floor(s / 86400)} días`;
};

const AdvancedStats = ({ devices = [] }) => {
  const stats = useMemo(() => {
    const total     = devices.length;
    const active    = devices.filter((d) => d.status === 'active').length;
    const suspended = devices.filter((d) => d.status === 'suspended').length;
    const offline   = devices.filter((d) => d.status === 'offline').length;
    const healthScore = total > 0 ? Math.round((active / total) * 100) : 0;

    const androidVersions = {};
    const manufacturers   = {};
    devices.forEach((d) => {
      const v = d.android_version || 'Desconocido';
      const m = d.manufacturer    || 'Desconocido';
      androidVersions[v] = (androidVersions[v] || 0) + 1;
      manufacturers[m]   = (manufacturers[m]   || 0) + 1;
    });

    const recentActivity = [...devices]
      .sort((a, b) => new Date(b.last_seen) - new Date(a.last_seen))
      .slice(0, 6)
      .map((d) => ({
        name:    d.device_name || 'Sin asignar',
        location:d.location    || 'Sin ubicación',
        time:    d.last_seen,
        status:  d.status,
      }));

    return { total, active, suspended, offline, healthScore, androidVersions, manufacturers, recentActivity };
  }, [devices]);

  if (stats.total === 0) return null;

  // Health Score color
  const hsColor =
    stats.healthScore >= 80 ? tokens.color.status.active.text
    : stats.healthScore >= 50 ? tokens.color.status.suspended.text
    : tokens.color.status.offline.text;

  const hsBg =
    stats.healthScore >= 80 ? tokens.color.status.active.bg
    : stats.healthScore >= 50 ? tokens.color.status.suspended.bg
    : tokens.color.status.offline.bg;

  return (
    <Grid container spacing={2}>
      {/* Health Score */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="overline" sx={{ mb: 2, display: 'block' }}>
              Salud del sistema
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  border: `4px solid ${hsBg}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  backgroundColor: hsBg,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '1.625rem',
                    fontWeight: 700,
                    color: hsColor,
                    lineHeight: 1,
                    letterSpacing: '-0.03em',
                  }}
                >
                  {stats.healthScore}
                  <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, color: hsColor }}>%</Typography>
                </Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ mb: 0.25 }}>
                  {stats.healthScore >= 80 ? 'Bueno' : stats.healthScore >= 50 ? 'Regular' : 'Crítico'}
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.color.ink.muted }}>
                  {stats.active} de {stats.total} activos
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <DistBar
              label="Activos"
              count={stats.active}
              total={stats.total}
              color={tokens.color.status.active.text}
            />
            <DistBar
              label="Suspendidos"
              count={stats.suspended}
              total={stats.total}
              color={tokens.color.status.suspended.text}
            />
            <DistBar
              label="Offline"
              count={stats.offline}
              total={stats.total}
              color={tokens.color.status.offline.text}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Versiones de Android */}
      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
              <Android sx={{ fontSize: 18, color: tokens.color.ink.muted }} />
              <Typography variant="overline">Versiones Android</Typography>
            </Box>
            {Object.entries(stats.androidVersions)
              .sort((a, b) => b[1] - a[1])
              .map(([version, count]) => (
                <DistBar
                  key={version}
                  label={`Android ${version}`}
                  count={count}
                  total={stats.total}
                  color={tokens.color.brand[500]}
                />
              ))}
          </CardContent>
        </Card>
      </Grid>

      {/* Fabricantes */}
      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
              <Memory sx={{ fontSize: 18, color: tokens.color.ink.muted }} />
              <Typography variant="overline">Fabricantes</Typography>
            </Box>
            {Object.entries(stats.manufacturers)
              .sort((a, b) => b[1] - a[1])
              .map(([manufacturer, count]) => (
                <DistBar
                  key={manufacturer}
                  label={manufacturer}
                  count={count}
                  total={stats.total}
                  color={tokens.color.ink.secondary}
                />
              ))}
          </CardContent>
        </Card>
      </Grid>

      {/* Actividad reciente */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Schedule sx={{ fontSize: 18, color: tokens.color.ink.muted }} />
              <Typography variant="overline">Actividad reciente</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: tokens.color.ink.muted, mb: 2 }}>
              Última señal de heartbeat por dispositivo
            </Typography>

            <List disablePadding>
              {stats.recentActivity.map((a, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <Divider />}
                  <ListItem
                    disableGutters
                    sx={{ py: 1.25, display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.ink.primary }}>
                          {a.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: tokens.color.ink.muted }}>
                          {a.location}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                      <Typography variant="caption" sx={{ color: tokens.color.ink.muted, whiteSpace: 'nowrap' }}>
                        {timeAgo(a.time)}
                      </Typography>
                      <StatusBadge status={a.status} />
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default AdvancedStats;
